// Utility to calculate dynamic valuation periods based on current date/time and exercise type

interface ValuationPeriodInfo {
  valuationPeriod: string;
  description: string;
  settlementPeriod: string;
  valuationEndDate: Date;
  settlementDate: Date;
}

// Helper function to get next trading day (skip weekends)
const getNextTradingDay = (date: Date): Date => {
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  
  // Skip weekends
  while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
    nextDay.setDate(nextDay.getDate() + 1);
  }
  
  return nextDay;
};

// Helper function to get trading days ahead
const getTradingDaysAhead = (startDate: Date, daysAhead: number): Date => {
  let currentDate = new Date(startDate);
  let tradingDaysCounted = 0;
  
  while (tradingDaysCounted < daysAhead) {
    currentDate = getNextTradingDay(currentDate);
    tradingDaysCounted++;
  }
  
  return currentDate;
};

// Helper function to check if date is a trading day (Monday-Friday)
const isTradingDay = (date: Date): boolean => {
  const day = date.getDay();
  return day >= 1 && day <= 5; // Monday = 1, Friday = 5
};

// Helper function to format date for display
const formatDateForDisplay = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  return date.toLocaleDateString('en-US', options);
};

// Helper function to check if current time is before cutoff
const isBeforeCutoff = (cutoffHour: number): boolean => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTime = currentHour * 100 + currentMinute;
  return currentTime < cutoffHour * 100;
};

export const calculateValuationPeriod = (exerciseType: 'VWAP' | 'RAPID'): ValuationPeriodInfo => {
  const now = new Date();
  
  if (exerciseType === 'VWAP') {
    // VWAP: 3 trading session valuation period
    const isBefore10AM = isBeforeCutoff(10);
    
    let valuationEndDate: Date;
    let settlementDate: Date;
    
    if (isBefore10AM && isTradingDay(now)) {
      // If before 10:00 AM EST and today is a trading day, today counts as day 1
      // Need 2 more trading days after today
      valuationEndDate = getTradingDaysAhead(now, 2);
    } else {
      // If after 10:00 AM EST or not a trading day, start from next trading day
      // Need 3 trading days total
      const nextTradingDay = getNextTradingDay(now);
      valuationEndDate = getTradingDaysAhead(nextTradingDay, 2);
    }
    
    // Settlement is same day as valuation end, 4-5 PM EST
    settlementDate = new Date(valuationEndDate);
    settlementDate.setHours(16, 0, 0, 0); // 4:00 PM
    
    // Set valuation end to 4:00 PM (market close)
    valuationEndDate.setHours(16, 0, 0, 0);
    
    return {
      valuationPeriod: '3 Trading Session Valuation Period',
      description: `If exercised now, the Valuation Period will begin and will be scheduled to conclude on ${formatDateForDisplay(valuationEndDate)} at 4pm EST (Market Close).`,
      settlementPeriod: `4pm-5pm, ${formatDateForDisplay(settlementDate)}.`,
      valuationEndDate,
      settlementDate
    };
    
  } else {
    // RAPID: 1 trading session valuation period
    const isBefore12PM = isBeforeCutoff(12);
    
    let valuationEndDate: Date;
    let settlementDate: Date;
    
    if (isBefore12PM && isTradingDay(now)) {
      // If before 12:00 PM EST and today is a trading day, today counts
      valuationEndDate = new Date(now);
    } else {
      // If after 12:00 PM EST or not a trading day, next trading day
      valuationEndDate = getNextTradingDay(now);
    }
    
    // Set to 4:00 PM (market close)
    valuationEndDate.setHours(16, 0, 0, 0);
    
    // Settlement is same day, 4-5 PM EST
    settlementDate = new Date(valuationEndDate);
    
    return {
      valuationPeriod: '1 Trading Session Valuation Period',
      description: `If exercised now, the Valuation Period will begin and will be scheduled to conclude on ${formatDateForDisplay(valuationEndDate)} at 4pm EST (Market Close).`,
      settlementPeriod: `4pm-5pm, ${formatDateForDisplay(settlementDate)}.`,
      valuationEndDate,
      settlementDate
    };
  }
}; 